import { z } from "zod";
import * as Crypto from "expo-crypto";
import { GRAVATAR_CACHE_TTL_MS } from "@/constants/cache";

export type Props = {
  email: string;
  size?: number;
  useIdenticon?: boolean;
};

// TTL-based cache to avoid repeated lookups for users without Gravatar
const GRAVATAR_TTL_CACHE = new Map<string, { url: string | null; timestamp: number }>();

export async function gravatarUrl({ email, size = 80, useIdenticon = false }: Props): Promise<string> {
  z.string().email().parse(email);
  const address = email.toLowerCase().trim();
  const hash = await Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, address);
  // d=404 returns 404 for non-existent gravatars, triggering Image.onError for initials fallback
  // d=identicon returns auto-generated pattern (opt-in via useIdenticon)
  const defaultParam = useIdenticon ? "identicon" : "404";
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=${defaultParam}`;
}

export async function tryGravatar(email: string, size = 80, useIdenticon = false): Promise<string | null> {
  try {
    // Validate email format
    z.string().email().parse(email);

    const cacheKey = `${email.toLowerCase().trim()}:${useIdenticon}`;
    const cached = GRAVATAR_TTL_CACHE.get(cacheKey);

    // Return cached result if within TTL
    if (cached && Date.now() - cached.timestamp < GRAVATAR_CACHE_TTL_MS) {
      return cached.url;
    }

    const url = await gravatarUrl({ email, size, useIdenticon });

    // If not using identicon fallback, verify gravatar exists via HEAD request
    // TTL cache ensures we don't repeat this check frequently (404s logged but infrequent)
    if (!useIdenticon) {
      const response = await fetch(url, { method: "HEAD" });
      if (!response.ok) {
        // Cache "not found" result to avoid repeated 404s
        GRAVATAR_TTL_CACHE.set(cacheKey, { url: null, timestamp: Date.now() });
        return null;
      }
    }

    // Cache successful result
    GRAVATAR_TTL_CACHE.set(cacheKey, { url, timestamp: Date.now() });
    return url;
  } catch {
    // Invalid email or fetch error - fail silently (expected for most users)
    return null;
  }
}

// Keep the default export for backwards compatibility
export default gravatarUrl;
